import { SupportedLanguages } from '@/i18n/i18next';
import {
  WizardFlow,
  WizardStep,
  WizardState,
  WizardNavigationHistory,
  WizardServiceInterface,
  ChatbotMessage,
} from '../types/wizard.types';
import {
  DebriefFormData,
  FeedbackFormData,
  InteractionFormData,
  PraiseFormData,
  WizardAnswers,
} from '@momentum/types';

export class WizardService implements WizardServiceInterface {
  private state: WizardState;
  private flow: WizardFlow<InteractionFormData>;
  private navigationHistory: WizardNavigationHistory[] = [];

  constructor(
    flow: WizardFlow<InteractionFormData>,
    initialLanguage: SupportedLanguages = 'en',
    initalState?: Partial<WizardState>,
    firstStepId?: string
  ) {
    this.flow = flow;
    const previousFlow = sessionStorage.getItem(flow.id);
    if (previousFlow) {
      const sessionState = JSON.parse(previousFlow);
      this.state = sessionState['state'];
      this.navigationHistory = sessionState['history'];
      this.injectDynamicStepsIfNeeded(this.state.currentStepId);
    } else {
      this.state = {
        currentStepId: flow.firstStepId,
        answers: {},
        language: initialLanguage,
        isCompleted: false,
        ...initalState,
      };
      // Process pre-filled answers in the order they appear in the flow
      // This ensures validation happens against the correct step
      const initialAnswers = { ...this.state.answers };
      this.state.answers = {}; // Clear answers temporarily
      this.state.currentStepId = flow.firstStepId; // Start from first step
      // Initialize navigation history with first step
      this.addToHistory(flow.firstStepId);

      // Process answers in flow order
      for (const step of flow.steps) {
        if (initialAnswers[step.id] !== undefined) {
          // Temporarily set current step to the step being answered
          this.state.currentStepId = step.id;
          // Process the answer (this will validate and advance to next step)
          this.answerStep(initialAnswers[step.id]);
          // If wizard completed, break
          if (this.state.isCompleted) {
            break;
          }
        }
      }
      if (firstStepId) {
        this.state.currentStepId = firstStepId;
      }
    }
  }

  // Get current state
  getState(): WizardState {
    return { ...this.state };
  }

  // Get current step
  getCurrentStep(): WizardStep | null {
    if (!this.state.currentStepId) return null;
    const step = this.flow.steps.find(
      (step) => step.id === this.state.currentStepId
    );
    if (!step) return null;

    // If title is a function, call it with current answers
    if (typeof step.title === 'function') {
      return {
        ...step,
        title: step.title(
          this.state.answers as WizardAnswers<
            PraiseFormData | DebriefFormData | FeedbackFormData
          >
        ),
      };
    }

    return step;
  }

  // Check if wizard is completed
  isCompleted(): boolean {
    return this.state.isCompleted;
  }

  // Get progress percentage
  getProgress(): number {
    const totalSteps = this.flow.steps.length;
    const completedSteps = Object.keys(this.state.answers).length;
    return Math.round((completedSteps / totalSteps) * 100);
  }

  // Start the wizard flow
  startFlow(): WizardStep | null {
    console.log(
      '🚀 Wizard: Starting flow with firstStepId:',
      this.flow.firstStepId
    );

    const firstStep = this.flow.steps.find(
      (step) => step.id === this.flow.firstStepId
    );
    if (!firstStep) {
      console.error('❌ Wizard: First step not found:', this.flow.firstStepId);
      return null;
    }

    this.state = {
      ...this.state,
      currentStepId: firstStep.id,
      isCompleted: false,
      error: undefined,
    };

    this.addToHistory(firstStep.id);
    console.log('🚀 Wizard: Flow started, current step:', firstStep.id);
    return firstStep;
  }

  // Answer current step and move to next
  answerStep(answer: any): WizardStep | null {
    const currentStep = this.updateStep(answer);
    if (!currentStep) {
      return null;
    }

    // Determine next step
    const nextStepId = this.getNextStepId(currentStep, answer);
    console.log('🎯 Wizard: Next step ID:', nextStepId);

    if (!nextStepId) {
      // Flow completed
      this.state.isCompleted = true;
      this.state.currentStepId = '';
      console.log('✅ Wizard: Flow completed');

      // Call completion callback if provided
      if (this.flow.onComplete) {
        this.flow.onComplete(this.state.answers);
      }

      this.saveState();
      return null;
    }

    // Move to next step
    const nextStep = this.flow.steps.find((step) => step.id === nextStepId);
    if (!nextStep) {
      console.error('❌ Wizard: Next step not found:', nextStepId);
      return null;
    }

    this.state.currentStepId = nextStepId;
    this.addToHistory(nextStepId);

    // If entering chatbot mode or continuing in the same group, add bot message
    if (nextStep.bot) {
      // Check if we're starting a new chatbot group
      if (nextStep.bot.group !== this.state.bot?.currentGroup) {
        // Clear messages for new group
        this.state.bot = {
          messages: [],
          currentGroup: nextStep.bot.group,
        };
      }

      // Add bot question
      const title =
        typeof nextStep.title === 'function'
          ? nextStep.title(
              this.state.answers as WizardAnswers<
                PraiseFormData | DebriefFormData | FeedbackFormData
              >
            )
          : nextStep.title;

      if (title) {
        this.addChatbotMessage({
          id: `${nextStep.id}-question`,
          type: 'bot',
          content: title,
          timestamp: Date.now(),
          stepId: nextStep.id,
        });
      }
    } else {
      // Leaving chatbot mode
      this.state.bot = undefined;
    }

    this.saveState();
    console.log('➡️ Wizard: Moved to step:', nextStepId);

    return nextStep;
  }

  updateStep(answer: any): WizardStep | null {
    const currentStep = this.getCurrentStep();
    if (!currentStep) {
      console.error('❌ Wizard: No current step to answer');
      return null;
    }

    // Validate the answer
    const validationError = this.validateCurrentStep(answer);
    if (validationError) {
      this.state.error = validationError;
      return currentStep;
    }

    // Clear any previous errors
    this.state.error = undefined;

    // Store the answer
    this.state.answers[this.state.currentStepId] = answer;
    console.log(
      '📝 Wizard: Answer stored for step:',
      this.state.currentStepId,
      answer
    );

    // If in chatbot mode, add user response as a message
    if (currentStep.bot) {
      const regex = /^debrief-match-point-(.+)-signal$/;
      const messageToFormat = regex.test(this.state.currentStepId)
        ? answer?.at(-1)?.text
        : answer;
      this.addChatbotMessage({
        id: `${this.state.currentStepId}-response`,
        type: 'user',
        content: this.formatAnswerForChatbotDisplay(messageToFormat),
        timestamp: Date.now(),
        stepId: this.state.currentStepId,
      });
    }

    // Check if we need to inject dynamic steps after this step
    this.injectDynamicStepsIfNeeded(this.state.currentStepId);

    this.saveState();
    console.log('➡️ Wizard: Update current step:');

    return currentStep;
  }

  // Add a chatbot message to the conversation
  private addChatbotMessage(message: ChatbotMessage): void {
    if (!this.state.bot) {
      this.state.bot = { messages: [] };
    }
    if (!this.state.bot.messages) {
      this.state.bot.messages = [];
    }
    this.state.bot.messages = [...this.state.bot.messages, message];
    console.log('chatbot messages', this.state.bot.messages);
  }

  // Format answer for chatbot display
  private formatAnswerForChatbotDisplay(answer: any): string {
    if (typeof answer === 'string') {
      return answer;
    }
    if (Array.isArray(answer)) {
      return answer.join(', ');
    }
    if (typeof answer === 'object' && answer !== null) {
      if (answer.skipped) {
        return "I don't know";
      }
      return Object.values(answer).join(', ').split('-').join(' ');
    }
    return String(answer);
  }

  // Inject dynamic steps if configured for this step
  private injectDynamicStepsIfNeeded(afterStepId: string): void {
    if (!this.flow.dynamicSteps) return;

    // Find dynamic step configurations for this step
    const dynamicConfigs = this.flow.dynamicSteps.filter(
      (config) =>
        config.afterStepId === afterStepId ||
        Object.keys(this.state.answers).includes(config.afterStepId)
    );

    if (dynamicConfigs.length === 0) return;

    dynamicConfigs.forEach((config) => {
      // Generate dynamic steps based on current answers
      const newSteps = config.generateSteps(this.state.answers);

      if (newSteps.length === 0) {
        console.log('🔄 Wizard: No dynamic steps generated');
        return;
      }

      console.log(
        `🔄 Wizard: Injecting ${newSteps.length} dynamic steps after ${afterStepId}`
      );

      // Find the index where we should insert the new steps
      const afterStepIndex = [...this.flow.steps, ...newSteps].findIndex(
        (s) => s.id === afterStepId
      );

      if (afterStepIndex === -1) {
        console.error(
          '❌ Wizard: Could not find step to inject after:',
          afterStepId
        );
        return;
      }

      // Remove any previously injected dynamic steps from the same config
      // (in case user goes back and changes their answer)
      const firstDynamicStep = newSteps[0];
      if (firstDynamicStep) {
        const existingDynamicStepIndex = this.flow.steps.findIndex(
          (s) => s.id === firstDynamicStep.id
        );

        if (existingDynamicStepIndex !== -1) {
          // Remove old dynamic steps
          const oldDynamicSteps = this.flow.steps.filter(
            (s) =>
              s.bot?.group &&
              newSteps.some((ns) => ns.bot?.group === s.bot?.group)
          );
          this.flow.steps = this.flow.steps.filter(
            (s) => !oldDynamicSteps.includes(s)
          );
        }
      }

      // Insert new dynamic steps after the trigger step
      this.flow.steps.splice(afterStepIndex + 1, 0, ...newSteps);

      console.log('✅ Wizard: Dynamic steps injected successfully');
    });
  }

  // Go to previous step
  goToPreviousStep(): WizardStep | null {
    if (this.navigationHistory.length <= 1) {
      console.log('⬅️ Wizard: No previous step available');
      return null;
    }

    const currentStepId = this.state.currentStepId;
    this.navigationHistory.pop();

    const previousHistoryEntry =
      this.navigationHistory[this.navigationHistory.length - 1];
    if (!previousHistoryEntry) {
      console.log('⬅️ Wizard: No previous step in history');
      return null;
    }

    const previousStep = this.flow.steps.find(
      (step) => step.id === previousHistoryEntry.stepId
    );
    if (!previousStep) {
      console.error(
        '❌ Wizard: Previous step not found:',
        previousHistoryEntry.stepId
      );
      return null;
    }

    // Clean up messages: remove all from current step, keep bot questions from previous step
    let updatedBot = this.state.bot;
    if (this.state.bot?.messages && this.state.bot.messages.length > 0) {
      const filteredMessages = this.state.bot.messages.filter(
        (message) =>
          message.stepId !== currentStepId &&
          !(message.stepId === previousStep.id && message.type === 'user')
      );
      updatedBot = {
        ...this.state.bot,
        messages: filteredMessages,
      };
    }

    // Clear answers for both current and previous steps
    const { [currentStepId]: _, ...updatedAnswers } = this.state.answers;

    // Update state with all changes at once
    this.state = {
      ...this.state,
      currentStepId: previousStep.id,
      error: undefined,
      bot: updatedBot,
      answers: updatedAnswers,
    };

    this.saveState();
    console.log('⬅️ Wizard: Moved to previous step:', previousStep.id);

    return previousStep;
  }

  // Go to specific step
  goToStep(stepId: string): WizardStep | null {
    const step = this.flow.steps.find((s) => s.id === stepId);
    if (!step) {
      console.error('❌ Wizard: Step not found:', stepId);
      return null;
    }

    this.state.currentStepId = stepId;
    this.state.error = undefined;
    this.addToHistory(stepId);
    console.log('🎯 Wizard: Jumped to step:', stepId);

    return step;
  }

  // Validate current step answer
  validateCurrentStep(answer: any): string | null {
    const currentStep = this.getCurrentStep();
    if (!currentStep) return 'No current step';

    // Run custom validation if provided
    if (currentStep.validation) {
      return currentStep.validation(answer);
    }

    // Default validation - check if answer is provided for non-skipable steps
    if (
      !currentStep.skipable &&
      (answer === undefined || answer === null || answer === '')
    ) {
      return 'This field is required';
    }

    return null;
  }

  // Reset wizard to initial state
  reset(): void {
    this.clearState();
    this.state = { ...this.state, currentStepId: '', isCompleted: false };
    // this.state = {
    //   currentStepId: '',
    //   answers: {},
    //   language: this.state.language, // Preserve language
    //   isCompleted: false,
    // };
    // this.navigationHistory = [];
    console.log('🔄 Wizard: Reset completed');
  }

  // Set language
  setLanguage(language: SupportedLanguages): void {
    this.state.language = language;
    console.log('🌐 Wizard: Language set to:', language);
  }

  // Private helper to determine next step
  private getNextStepId(step: WizardStep, answer: any): string | null {
    if (typeof step.nextStep === 'function') {
      return step.nextStep(answer, this.state.answers);
    }

    return step.nextStep || null;
  }

  // Private helper to add step to navigation history
  private addToHistory(stepId: string): void {
    this.navigationHistory.push({
      stepId,
      timestamp: Date.now(),
    });

    // Keep history manageable (max 50 entries)
    if (this.navigationHistory.length > 50) {
      this.navigationHistory = this.navigationHistory.slice(-50);
    }
  }

  // Get navigation history (useful for debugging)
  getNavigationHistory(): WizardNavigationHistory[] {
    return [...this.navigationHistory];
  }

  // Check if previous step is available
  canGoBack(): boolean {
    return this.navigationHistory.length > 1;
  }

  // Get step by ID
  getStepById(stepId: string): WizardStep | null {
    return this.flow.steps.find((step) => step.id === stepId) || null;
  }

  // Get flow information
  getFlowInfo(): { id: string; title?: string; description?: string } {
    return {
      id: this.flow.id,
      title: this.flow.title,
      description: this.flow.description,
    };
  }

  saveState(): void {
    const sessionState = {
      history: this.navigationHistory,
      state: this.state,
    };
    sessionStorage.setItem(this.flow.id, JSON.stringify(sessionState));
    console.log('✅ Wizard: Save state to session storage');
  }

  clearState(): void {
    sessionStorage.removeItem(this.flow.id);
  }
}
